<template>
    <div class="forum-wrapper">
      <div class="forum-container">
        <!-- Header -->
        <header class="forum-header">
          <h1 class="typo-headline-4">Buscar en el Foro</h1>
        </header>
  
        <!-- Search Form -->
        <p v-if="$route.query.experience_search" class="typo-body-2" role="status">Los filtros se conservan. Esta versión muestra primero los temas y después las respuestas; {{ $route.query.experience_search === 'restored' ? 'hemos recuperado tu página anterior.' : 'empiezas en la primera página.' }}</p>
        <p v-if="sectionError" class="error-message typo-body-2" role="alert">{{ sectionError }} <button type="button" @click="fetchSections">Reintentar secciones</button></p>
        <section class="search-form">
          <form @submit.prevent="newSearch">
            <div class="form-group">
              <label for="keyword" class="typo-body-2">Palabra clave:</label>
              <input type="text" id="keyword" v-model="keyword" class="typo-body-2"
                placeholder="Introduce la frase de búsqueda" required />
            </div>
  
            <div class="form-group">
              <label for="section" class="typo-body-2">Categoría del foro:</label>
              <select id="section" v-model="selectedSection" class="typo-body-2">
                <option value="">Todas las categorías</option>
                <option v-for="section in sections" :key="section.tid" :value="section.tid">
                  {{ section.name }}
                </option>
              </select>
            </div>
  
            <div class="form-group">
              <label class="typo-body-2">
                <input type="checkbox" v-model="exact" />
                Búsqueda exacta
              </label>
              <small class="typo-caption">
                (Marcar para buscar la frase exacta completa. Si no se marca, se
                buscará cada palabra de la frase en cualquier parte del texto.)
              </small>
            </div>
  
            <!-- These checkboxes modify the query only; the displayed results remain unchanged until a new search is executed -->
            <div class="form-group">
              <label class="typo-body-2">
                <input type="checkbox" v-model="includeTopics" />
                Incluir Temas
              </label>
            </div>
            <div class="form-group">
              <label class="typo-body-2">
                <input type="checkbox" v-model="includeComments" />
                Incluir Respuestas
              </label>
            </div>
  
            <div class="form-group">
              <button type="submit" class="typo-button">Buscar</button>
            </div>
          </form>
        </section>
  
        <!-- Search Results -->
        <section class="search-results" v-if="searched || loading || error">
          <div v-if="loading" class="loading-message typo-body-2">
            Buscando...
          </div>
          <div v-else-if="error" class="error-message typo-body-2" role="alert">
            {{ error }} <button type="button" @click="searchForum">Reintentar</button>
          </div>
          <div v-else>
            <!-- Combined Header when both Topics and Comments are enabled -->
            <div v-if="searchIncludeTopics && searchIncludeComments">
              <h2 class="typo-headline-6">
                <span v-if="currentPage < topicPages">Temas</span>
                <span v-else>Respuestas</span>
                ({{ combinedCount }})
              </h2>
            </div>
            <!-- Otherwise, show individual header -->
            <div v-else-if="searchIncludeTopics">
              <h2 class="typo-headline-6">Temas ({{ totalTopics }})</h2>
            </div>
            <div v-else-if="searchIncludeComments">
              <h2 class="typo-headline-6">Respuestas ({{ totalComments }})</h2>
            </div>
  
            <!-- Display results based on phased pagination:
                 When both are enabled, topics are shown on pages less than topicPages,
                 and responses are shown on pages >= topicPages. -->
            <div v-if="searchIncludeTopics && searchIncludeComments">
              <div v-if="currentPage < topicPages">
                <div v-if="resultsTopics.length">
                  <div v-for="topic in resultsTopics" :key="topic.nid" class="result-item"
                    @click="goToThread(topic)">
                    <h3 class="typo-headline-6" v-html="highlightText(topic.title)"></h3>
                    <div class="result-meta typo-caption">
                      <span>{{ formatTimestamp(topic.created) }}</span>
                    </div>
                    <p class="typo-body-2" v-html="highlightText(topic.body, true)"></p>
                  </div>
                </div>
                <div v-else class="typo-body-2">No se encontraron temas.</div>
              </div>
              <div v-else>
                <div v-if="resultsComments.length">
                  <div v-for="comment in resultsComments" :key="comment.cid" class="result-item"
                    @click="goToThread(comment)">
                    <h3 class="typo-headline-6" v-html="highlightText(comment.subject)"></h3>
                    <div class="result-meta typo-caption">
                      <span>{{ formatTimestamp(comment.created) }}</span>
                    </div>
                    <p class="typo-body-2" v-html="highlightText(comment.body, true)"></p>
                  </div>
                </div>
                <div v-else class="typo-body-2">No se encontraron respuestas.</div>
              </div>
            </div>
            <!-- When only one type is enabled, show that section -->
            <div v-else-if="searchIncludeTopics">
              <div v-if="resultsTopics.length">
                <div v-for="topic in resultsTopics" :key="topic.nid" class="result-item"
                  @click="goToThread(topic)">
                  <h3 class="typo-headline-6" v-html="highlightText(topic.title)"></h3>
                  <div class="result-meta typo-caption">
                    <span>{{ formatTimestamp(topic.created) }}</span>
                  </div>
                  <p class="typo-body-2" v-html="highlightText(topic.body, true)"></p>
                </div>
              </div>
              <div v-else class="typo-body-2">No se encontraron temas.</div>
            </div>
            <div v-else-if="searchIncludeComments">
              <div v-if="resultsComments.length">
                <div v-for="comment in resultsComments" :key="comment.cid" class="result-item"
                  @click="goToThread(comment)">
                  <h3 class="typo-headline-6" v-html="highlightText(comment.subject)"></h3>
                  <div class="result-meta typo-caption">
                    <span>{{ formatTimestamp(comment.created) }}</span>
                  </div>
                  <p class="typo-body-2" v-html="highlightText(comment.body, true)"></p>
                </div>
              </div>
              <div v-else class="typo-body-2">No se encontraron respuestas.</div>
            </div>
  
            <!-- Pagination Controls -->
            <div class="results-pagination">
              <button @click="prevPage" :disabled="currentPage === 0" class="pagination-btn typo-button">
                Anterior
              </button>
              <span class="page-info typo-body-2">
                Página {{ currentPage + 1 }} de {{ totalPages }}
              </span>
              <button @click="nextPage" :disabled="currentPage + 1 >= totalPages"
                class="pagination-btn typo-button">
                Siguiente
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </template>
  
<script>
import { legacyForumSearch } from '@/features/forum/services/forumLegacySearch';
import { legacySearchHighlight } from '@/features/forum/services/forumSearchHighlight';
export default {
  name: 'ForumSearch',
  mixins: [legacyForumSearch({ multipart: true })],
  methods: {
    formatTimestamp(ts) { return new Date(ts * 1000).toLocaleString(); },
    goToThread(item) {
      this.$router.push({ name: 'ForumThread', params: { nid: item.nid },
        ...(item.cid ? { query: { comment_id: String(item.cid) }, hash: '#comment-' + item.cid } : {}) });
    },
    highlightText(text, html = false) { return legacySearchHighlight(text, this.searchedKeyword, { html, exact: this.searchExact }); }
  }
};
</script>
  
  <style scoped>
  /* Overall container */
  .forum-wrapper {
    background-color: var(--color-background);
    min-height: 100vh;
    padding: 40px 20px;
  }
  
  /* Centered container */
  .forum-container {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    max-width: 800px;
    margin: 0 auto;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 6px var(--color-outline);
  }
  
  /* Header */
  .forum-header {
    text-align: center;
    margin-bottom: 20px;
    border-bottom: 1px solid var(--color-outline);
    padding-bottom: 10px;
  }
  
  /* Search Form */
  .search-form {
    margin-bottom: 30px;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  input[type="text"],
  select {
    width: 100%;
    padding: 8px;
    border: 1px solid var(--color-outline);
    border-radius: 4px;
    background-color: var(--color-background);
    color: var(--color-text-primary);
  }
  
  input[type="text"] {
    padding-left: 0;
    padding-right: 0;
    text-indent: 8px;
  }
  
  input[type="checkbox"] {
    margin-right: 5px;
  }
  
  /* Button styles */
  button.typo-button {
    background-color: var(--color-background);
    color: var(--color-text-primary);
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  button.typo-button:hover {
    background-color: var(--color-highlight);
  }
  
  /* Search results */
  .search-results h2 {
    margin-top: 20px;
    margin-bottom: 10px;
  }
  
  .result-item {
    padding: 15px;
    border-bottom: 1px solid var(--color-outline);
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  
  .result-item:hover {
    background-color: var(--color-active-surface);
  }
  
  .result-meta {
    margin-bottom: 10px;
  }
  
  /* Pagination Controls */
  .results-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
    margin-top: 20px;
  }
  
  .pagination-btn {
    background-color: var(--color-primary);
    color: var(--color-text-primary);
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination-btn:hover:not(:disabled) {
    background-color: var(--color-highlight);
  }
  
  .page-info {
    font-size: 0.95rem;
  }
  
  /* Deep selector for injected HTML (v-html) so that .highlight is applied */
  >>> .highlight {
    background-color: var(--color-highlight);
    color: var(--color-background);
  }
  </style>
